import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export const GoogleDriveIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M10.2 22.5L16 12.2l5.8 10.3H10.2z" fill="#FFBA00" />
    <path d="M7 22.5l4.2-7.4L16 22.5H7z" fill="#188038" />
    <path d="M16 22.5l4.8-7.4L25 22.5H16z" fill="#1A73E8" />
    <path d="M11.2 15.1L16 7.5l4.8 7.6H11.2z" fill="#EA4335" />
  </svg>
);

const DriveFolderNameModal = ({
  isOpen,
  fileCount = 0,
  value = '',
  onChange,
  onClose,
  onConfirm,
  saving = false,
  uploaded = 0,
  total = 0,
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (isOpen) {
      setLocalValue(value);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen, value]);

  if (!isOpen) return null;

  const trimmed = String(localValue || '').trim();
  const progressTotal = total > 0 ? total : fileCount;
  const percent = progressTotal > 0 ? Math.min(100, Math.round((uploaded / progressTotal) * 100)) : 0;
  const submit = () => {
    if (!trimmed || saving) return;
    onChange?.(trimmed);
    onConfirm?.(trimmed);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2100] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={saving ? undefined : onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 min-w-0">
              <GoogleDriveIcon className="w-6 h-6 shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">Save to Google Drive</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form
            className="p-6 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {fileCount} file{fileCount === 1 ? '' : 's'} will be saved in this Drive folder. The folder link is what we share in chat.
            </p>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Folder name</span>
              <input
                ref={inputRef}
                type="text"
                value={localValue}
                maxLength={120}
                disabled={saving}
                onChange={(event) => {
                  setLocalValue(event.target.value);
                  onChange?.(event.target.value);
                }}
                placeholder="e.g. Site photos"
                className="w-full text-sm px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500"
              />
            </label>
            {saving ? (
              <div className="rounded-xl border border-blue-100 dark:border-blue-900/50 bg-blue-50/80 dark:bg-blue-950/30 px-3 py-3 space-y-2">
                <div className="flex items-center gap-2">
                  <GoogleDriveIcon className="w-5 h-5 shrink-0" />
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {uploaded}/{progressTotal} uploaded
                  </p>
                  <span className="ml-auto text-xs font-medium text-gray-500">{percent}%</span>
                </div>
                <div className="h-2 rounded-full bg-white dark:bg-gray-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#1A73E8] transition-all duration-300"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Saving files to Google Drive…
                </p>
              </div>
            ) : null}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!trimmed || saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
              >
                <GoogleDriveIcon className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save folder'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DriveFolderNameModal;
